/**
 * Phase 9bis-d — Section projet réutilisable pour le Hub Client.
 *
 * Rend les devis + factures + chantiers d'UN projet donné. Deux modes :
 * - `mode="flat"` : affichage sans accordéon (pour le cas 1 projet, style Phase 3 identique)
 * - `mode="accordion"` : affichage repliable avec header (titre projet + stage) — pour multi-projets
 */
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Receipt,
  Hammer,
  Download,
  ExternalLink,
  PenLine,
  CreditCard,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  CalendarClock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const money = (n) =>
  new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const shortDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("fr-BE", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

// Doctrine FR/EN (12/09/2026) — clés = FR exact base de données (`signe`, `refuse`, `expire`).
// Alias EN legacy conservés en couche défensive (au cas où un doc legacy sortirait avec l'ancienne clé).
const QUOTE_STATUS = {
  envoye: { label: "Envoyé", cls: "badge-warning" },
  signe: { label: "Signé", cls: "badge-success" },
  refuse: { label: "Refusé", cls: "badge-neutral" },
  expire: { label: "Expiré", cls: "badge-neutral" },
  // Alias legacy EN (défensif — pas de clé primaire)
  signed: { label: "Signé", cls: "badge-success" },
  refused: { label: "Refusé", cls: "badge-neutral" },
  expired: { label: "Expiré", cls: "badge-neutral" },
};
// Phase Semi-Auto Facture (12/09/2026) — statuts `a_envoyer` + `envoyee` remplacent `pending`.
const INVOICE_STATUS = {
  a_envoyer: { label: "À envoyer", cls: "badge-info" },
  envoyee: { label: "Envoyée", cls: "badge-warning" },
  overdue: { label: "En retard", cls: "badge-error" },
  paid: { label: "Payée", cls: "badge-success" },
  // Alias legacy (défensif — factures pré-migration éventuelles)
  pending: { label: "Envoyée", cls: "badge-warning" },
};

const STAGE_LABEL = {
  en_attente: "En attente",
  contacte: "Contacté",
  rdv_fixe: "Visite planifiée",
  devis_envoye: "Devis envoyé",
  en_negociation: "En négociation",
  signe: "Signé",
  perdu: "Perdu",
};

function SectionHeader({ icon, title, count, accent }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      {icon}
      <h3 className="text-base font-semibold text-[#2A2420]">{title}</h3>
      {typeof count === "number" && count > 0 && (
        <span className="text-sm text-[#5B5148]">({count})</span>
      )}
    </div>
  );
}

function QuoteCard({ q, accent, testidPrefix }) {
  const st = QUOTE_STATUS[q.status] || QUOTE_STATUS.envoye;
  const canSign = q.status === "envoye";
  return (
    <Card className="border-[#E5DDD1] bg-white" data-testid={`${testidPrefix}-quote-card-${q.id}`}>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-xs text-[#5B5148]">{q.number || "brouillon"}</span>
            <span className="font-semibold">{q.title}</span>
            <Badge className={`border ${st.cls}`}>{st.label}</Badge>
            {q.is_amendment && (
              <Badge
                className="border border-[#C4553B]/60 bg-[#C4553B]/10 text-[#C4553B]"
                data-testid={`${testidPrefix}-quote-amendment-badge-${q.id}`}
              >
                COMPLÉMENT
              </Badge>
            )}
          </div>
          <div className="mt-1 text-sm text-[#5B5148]">
            <span className="tabular-nums font-medium text-[#2A2420]">{money(q.total_tvac)}</span>
            <span className="text-xs"> · Émis {shortDate(q.issued_at)}</span>
            {q.valid_until && <span className="text-xs"> · Valable jusqu&apos;au {shortDate(q.valid_until)}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            asChild
            variant="outline"
            size="sm"
            className="border-[#D9CFC1] bg-white text-[#2A2420] hover:bg-[#F0EAE0]"
          >
            <a href={`${API}${q.view_url}`} target="_blank" rel="noopener noreferrer" data-testid={`${testidPrefix}-quote-view-${q.id}`}>
              <ExternalLink className="mr-1 h-3.5 w-3.5" /> Consulter
            </a>
          </Button>
          {canSign && (
            <Button asChild size="sm" className="text-white" style={{ background: accent }}>
              <a href={`${API}${q.view_url}?action=sign`} target="_blank" rel="noopener noreferrer" data-testid={`${testidPrefix}-quote-sign-${q.id}`}>
                <PenLine className="mr-1 h-3.5 w-3.5" /> Signer
              </a>
            </Button>
          )}
          {q.pdf_url && (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-[#D9CFC1] bg-white text-[#2A2420] hover:bg-[#F0EAE0]"
            >
              <a href={`${API}${q.pdf_url}`} target="_blank" rel="noopener noreferrer" data-testid={`hub-download-pdf-btn-${q.id}`}>
                <Download className="mr-1 h-3.5 w-3.5" /> Télécharger le PDF
              </a>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function InvoiceCard({ inv, accent, companyIban, onPay, testidPrefix }) {
  const st = INVOICE_STATUS[inv.status] || INVOICE_STATUS.a_envoyer;
  return (
    <Card className="border-[#E5DDD1] bg-white" data-testid={`${testidPrefix}-invoice-card-${inv.id}`}>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-xs text-[#5B5148]">{inv.number || "—"}</span>
            <span className="font-semibold capitalize">{inv.type}</span>
            <Badge className={`border ${st.cls}`}>{st.label}</Badge>
          </div>
          <div className="mt-1 text-sm text-[#5B5148]">
            <span className="tabular-nums font-medium text-[#2A2420]">{money(inv.amount_tvac)}</span>
            <span className="text-xs"> · Émise {shortDate(inv.issued_at)}</span>
            {inv.due_date && <span className="text-xs"> · Échéance {shortDate(inv.due_date)}</span>}
            {inv.paid_at && <span className="text-xs text-emerald-700"> · Payée {shortDate(inv.paid_at)}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {inv.pdf_url && (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-[#D9CFC1] bg-white text-[#2A2420] hover:bg-[#F0EAE0]"
            >
              <a href={`${API}${inv.pdf_url}`} target="_blank" rel="noopener noreferrer" data-testid={`hub-download-pdf-btn-${inv.id}`}>
                <Download className="mr-1 h-3.5 w-3.5" /> Télécharger le PDF
              </a>
            </Button>
          )}
          {inv.status !== "paid" && companyIban && (
            <Button
              size="sm"
              onClick={() => onPay(inv)}
              className="text-white"
              style={{ background: accent }}
              data-testid={`${testidPrefix}-invoice-pay-${inv.id}`}
            >
              <CreditCard className="mr-1 h-3.5 w-3.5" /> Coordonnées bancaires
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ChantierCard({ ch, accent, testidPrefix }) {
  // Phase 9bis-hub-proactif — Prochaine étape (date longue chaleureuse ou "à venir")
  // Regle produit : masquer tout le bloc si le chantier n'a pas encore été créé (pas ce cas ici,
  // car on est dans la boucle chantiers). Afficher toujours si chantier existe : date OU placeholder.
  const startDate = ch.expected_start_date || null;
  const startDateLong = startDate
    ? new Date(startDate + "T12:00:00").toLocaleDateString("fr-BE", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;
  const isChantierDone =
    ch.status === "termine" || ch.status === "finalise";

  return (
    <Card className="border-[#E5DDD1] bg-white" data-testid={`${testidPrefix}-chantier-card-${ch.id}`}>
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-semibold">{ch.title || "Chantier"}</span>
              <Badge className="border border-[#D9CFC1] bg-[#F7F3EC] text-[#5B5148]">
                {(ch.status || "").replaceAll("_", " ")}
              </Badge>
              {ch.recap_signed && (
                <Badge className="border border-emerald-600/40 bg-emerald-100 text-emerald-700">
                  <ShieldCheck className="mr-1 h-3 w-3" /> Récap signé
                </Badge>
              )}
            </div>
            <div className="mt-1 text-sm text-[#5B5148]">
              {ch.current_step && <span>Étape : {ch.current_step} · </span>}
              <span>{ch.updates_count || 0} mise(s) à jour</span>
            </div>
          </div>
          {ch.view_url && (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-[#D9CFC1] bg-white text-[#2A2420] hover:bg-[#F0EAE0]"
            >
              <a href={`${API}${ch.view_url}`} target="_blank" rel="noopener noreferrer" data-testid={`${testidPrefix}-chantier-view-${ch.id}`}>
                <ExternalLink className="mr-1 h-3.5 w-3.5" /> Suivre le chantier
              </a>
            </Button>
          )}
        </div>

        {/* Phase 9bis-hub-proactif — Prochaine étape (rassurant, chaleureux) */}
        {!isChantierDone && (
          <div
            className="flex items-start gap-3 rounded-md border border-[#E5DDD1] bg-[#F7F3EC] px-4 py-3"
            data-testid={`${testidPrefix}-chantier-next-step-${ch.id}`}
          >
            <CalendarClock
              className="mt-0.5 h-4 w-4 flex-shrink-0"
              style={{ color: accent }}
            />
            <div className="text-sm">
              <div className="text-xs uppercase tracking-wide text-[#5B5148]">
                Prochaine étape
              </div>
              {startDateLong ? (
                <div className="mt-0.5 text-[#2A2420]" data-testid={`${testidPrefix}-chantier-next-step-date-${ch.id}`}>
                  Vos travaux débutent le{" "}
                  <b>{startDateLong}</b>.
                </div>
              ) : (
                <div className="mt-0.5 text-[#5B5148]" data-testid={`${testidPrefix}-chantier-next-step-pending-${ch.id}`}>
                  Nous vous communiquerons la date de démarrage prochainement.
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * ProjectSection — rend les devis/factures/chantiers d'un projet.
 *
 * Props :
 *  - project : { id, title, stage, quotes[], invoices[], chantiers[] }
 *  - accent  : couleur accent de l'entreprise
 *  - companyIban : IBAN pour afficher/masquer le bouton "Coordonnées bancaires"
 *  - onOpenBankModal(invoice) : callback pour ouvrir la modal IBAN
 *  - mode : "flat" | "accordion"
 *  - defaultOpen : bool (accordion only) — ouvert à l'affichage initial ?
 */
export default function ProjectSection({
  project,
  accent,
  companyIban,
  onOpenBankModal,
  mode = "flat",
  defaultOpen = true,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const p = project;
  const testidPrefix = mode === "accordion" ? `hub-project-${p.id}` : "hub";
  const hasQuotes = (p.quotes || []).length > 0;
  const hasInvoices = (p.invoices || []).length > 0;
  const hasChantiers = (p.chantiers || []).length > 0;

  const body = (
    <div className="space-y-6">
      <section data-testid={`${testidPrefix}-quotes-section`}>
        <SectionHeader
          icon={<FileText className="h-5 w-5" style={{ color: accent }} />}
          title="Devis"
          count={(p.quotes || []).length}
          accent={accent}
        />
        {hasQuotes ? (
          <div className="space-y-3">
            {p.quotes.map((q) => (
              <QuoteCard key={q.id} q={q} accent={accent} testidPrefix={testidPrefix} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#5B5148]">Votre devis vous sera envoyé sous peu.</p>
        )}
      </section>

      {(hasInvoices || mode === "flat") && (
        <section data-testid={`${testidPrefix}-invoices-section`}>
          <SectionHeader
            icon={<Receipt className="h-5 w-5" style={{ color: accent }} />}
            title="Factures"
            count={(p.invoices || []).length}
            accent={accent}
          />
          {hasInvoices ? (
            <div className="space-y-3">
              {p.invoices.map((inv) => (
                <InvoiceCard
                  key={inv.id}
                  inv={inv}
                  accent={accent}
                  companyIban={companyIban}
                  onPay={onOpenBankModal}
                  testidPrefix={testidPrefix}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#5B5148]">Aucune facture pour le moment.</p>
          )}
        </section>
      )}

      {hasChantiers && (
        <section data-testid={`${testidPrefix}-chantiers-section`}>
          <SectionHeader
            icon={<Hammer className="h-5 w-5" style={{ color: accent }} />}
            title={(p.chantiers || []).length > 1 ? "Chantiers" : "Chantier"}
            count={(p.chantiers || []).length}
            accent={accent}
          />
          <div className="space-y-3">
            {p.chantiers.map((c) => (
              <ChantierCard key={c.id} ch={c} accent={accent} testidPrefix={testidPrefix} />
            ))}
          </div>
        </section>
      )}
    </div>
  );

  if (mode === "flat") {
    return <div data-testid={`hub-project-flat-${p.id}`}>{body}</div>;
  }

  // Accordion mode
  return (
    <div
      className="overflow-hidden rounded-lg border border-[#E5DDD1] bg-white"
      data-testid={`hub-project-accordion-${p.id}`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 border-b border-transparent px-4 py-3 text-left hover:bg-[#F7F3EC] data-[open=true]:border-[#E5DDD1]"
        data-open={open}
        data-testid={`hub-project-toggle-${p.id}`}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-[#5B5148]" />
        ) : (
          <ChevronRight className="h-4 w-4 text-[#5B5148]" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-base font-semibold text-[#2A2420]">{p.title || "Projet"}</span>
            <Badge className="border border-[#D9CFC1] bg-[#F7F3EC] text-[#5B5148]">
              {STAGE_LABEL[p.stage] || (p.stage || "").replaceAll("_", " ")}
            </Badge>
          </div>
          <div className="mt-0.5 text-xs text-[#5B5148]">
            {(p.quotes || []).length} devis · {(p.invoices || []).length} facture{((p.invoices || []).length > 1) ? "s" : ""}
            {hasChantiers && ` · ${p.chantiers.length} chantier${p.chantiers.length > 1 ? "s" : ""}`}
          </div>
        </div>
      </button>
      {open && <div className="px-4 py-4 sm:px-6">{body}</div>}
    </div>
  );
}
